import { useNavigate } from "react-router-dom";
import React, { useEffect, useRef, useState } from "react";
import { Button } from "../../../../Common/Components/BaseComponents/Button";
import { PageContainer } from "../../../../Common/Components/BaseComponents/PageContainer";
import { ExportToExcel } from "../exportToExcel";
import { Stack, TextField } from "@mui/material";
import debounce from "lodash/debounce";
import { showAlert } from "../../../../Common/Components/BaseComponents/ShowAlert";
import { ShowAlertWarn } from "../../../../Common/Components/BaseComponents/showAlertWarn";
import { useCustomerStore } from "../../../../Stores/LeadAndCustomer/customerStore";
import { ExpandableCustomerCard } from "../../../../Common/Components/BaseComponents/ExpandableCard";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AddIcon from "@mui/icons-material/Add";
import UploadIcon from "@mui/icons-material/Upload";

import { CustomerStatus, PaymentMethodType, WorkspaceType } from "shared-types";

interface ValuesToTable {
    id: string;
}

export interface CustomerCardProps {
    id: string;
    name: string;
    phone: string;
    email: string;
    businessName: string;
    businessType: string;
    status: CustomerStatus;
    image?: string;
    idNumber?: string;
    currentWorkspaceType?: WorkspaceType;
    workspaceCount?: number;
    contractSignDate?: string;
    contractStartDate?: string;
    billingStartDate?: string;
    notes?: string;
    invoiceName?: string;
    contractDocumentUrl?: string;
    paymentMethodType?: PaymentMethodType;
    createdAt?: string;
    updatedAt?: string;
    onEdit: (id: string) => void;
    onDelete: (id: string) => void;
}

export const CustomersList = () => {
    const navigate = useNavigate();
    const loaderRef = useRef<HTMLDivElement | null>(null);

    const [searchTerm, setSearchTerm] = useState("");

    const {
        customers,
        deleteCustomer,
        loading,
        currentPage,
        limit,
        searchCustomersByText,
        fetchCustomersByPage,
        searchCustomersInPage,
        fetchNextPage,
        fetchPrevPage,
    } = useCustomerStore();

    useEffect(() => {
        fetchCustomersByPage();
    }, [fetchCustomersByPage]);

    const deleteCurrentCustomer = async (val: ValuesToTable) => {
        const confirmed = await ShowAlertWarn(
            "האם אתה בטוח שברצונך למחוק את הלקוח לצמיתות?",
            "לא ניתן לשחזר את המידע לאחר מחיקה.",
            "warning"
        );
        if (confirmed) {
            await deleteCustomer(val.id);
            showAlert("מחיקה", "לקוח נמחק בהצלחה", "success");
            const latestError = useCustomerStore.getState().error;
            if (latestError) {
                const errorMessage = latestError || "שגיאה בלתי צפויה";
                console.error("Error:", errorMessage);
                showAlert("שגיאה במחיקת לקוח", errorMessage, "error");
            }
        }
    };

    const editCustomer = (val: ValuesToTable) => {
        const selected = customers.find((c) => c.id === val.id);
        console.log("selected customer", selected);
        navigate("update", { state: { data: selected } });
    };

    // הפונקציה שמטפלת בשינוי החיפוש
    const handleSearch = (term: string) => {
        searchCustomersInPage(term).then(() => {
        });
    };

    const debouncedSearch = useRef(
        debounce((value: string) => handleSearch(value), 400)
    ).current;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;

        setSearchTerm(value);
        debouncedSearch(value);
    };

    const searchInApi = async (e: { key: string }) => {
        //איך ידעו שבלחיצה על אנטר זה מחפש בשרת?...
        if (
            (e.key === "Enter" && searchTerm.trim()) ||
            customers.length === 0 // אין תוצאות בדף הנוכחי
        ) {
            console.log("🔍 חיפוש בשרת עם המחרוזת:", searchTerm);

            await searchCustomersByText(searchTerm);
            // .then(() => {
            //   console.log("✅ תוצאות שהגיעו מהשרת:", customers.length);
            // }).catch((error) => {
            //   console.error("שגיאה בחיפוש מהשרת:", error);
            // });
        }
    };

    const getCardData = () => {
        return customers.map((c) => ({
            id: c.id!,
            name: c.name,
            phone: c.phone,
            email: c.email ?? "",
            businessName: c.businessName,
            businessType: c.businessType,
            status: c.status,
            idNumber: c.idNumber,
            currentWorkspaceType: c.currentWorkspaceType,
            workspaceCount: c.workspaceCount,
            contractSignDate: c.contractSignDate,
            contractStartDate: c.contractStartDate,
            billingStartDate: c.billingStartDate,
            notes: c.notes,
            invoiceName: c.invoiceName,
            contractDocumentUrl: c.contractDocumentUrl,
            paymentMethodType: c.paymentMethodType,
            ip: c.ip,
            createdAt: c.createdAt,
            updatedAt: c.updatedAt,
        }));
    };

    return (
        <PageContainer title="לקוחות">

            <div className="flex items-center gap-4 mb-4">
                <Button
                    color="green"
                    size="sm"
                    onClick={ () => navigate("new") }
                    icon={<AddIcon style={{ fontSize: 16 }}/>}
                >
                    הוספת לקוח חדש
                </Button>
                <Button
                    onClick={ () =>
                        navigate("/leadAndCustomer/Customers/UploadCustomersFile")
                    }
                    color="blue"
                    size="sm"
                    icon={<UploadIcon style={{ fontSize: 16 }}/>}
                >
                    יבוא מאקסל
                </Button>
                <div className="flex items-center">
                    <ExportToExcel data={ customers } fileName="לקוחות"/>
                </div>
            </div>

            <br/>
            <Stack spacing={ 2 } direction="row">
                <TextField
                    label="חיפוש"
                    fullWidth
                    value={ searchTerm }
                    onChange={ handleChange }
                    onKeyDown={ searchInApi }
                    placeholder="חפש לקוחות לפי שם, טלפון, אימייל, שם עסק או סוג עסק"
                />
            </Stack>

            <div className="relative mt-6">
                <div className="grid gap-4">
                    { getCardData().map((customer) => (
                        <ExpandableCustomerCard
                            key={ customer.id }
                            { ...customer }
                            onEdit={ () => editCustomer({ id: customer.id }) }
                            onDelete={ () => deleteCurrentCustomer({ id: customer.id }) }
                        />
                    )) }
                </div>
                { loading && (
                    <div className="fixed inset-0 flex items-center justify-center bg-white bg-opacity-75 z-10">
                        <div
                            className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-500"></div>
                    </div>
                ) }
            </div>

            <div className="flex justify-center space-x-4 my-4 gap-4">
                <Button
                    color={ currentPage > 1 ? "blue" : "gray" }
                    disabled={ currentPage <= 1 }
                    onClick={ async () => {
                        if (currentPage > 1) {
                            await fetchPrevPage();
                        }
                    } }
                    icon={<ArrowForwardIcon style={{ fontSize: 16 }}/>}
                >
                    הקודם
                </Button>
                <Button
                    color={ customers.length === limit ? "blue" : "gray" }
                    disabled={ customers.length < limit }
                    onClick={ async () => {
                        if (customers.length === limit) {
                            await fetchNextPage();
                        }
                    } }
                    icon={<ArrowBackIcon style={{ fontSize: 16 }}/>}
                >
                    הבא
                </Button>
            </div>

            <div ref={ loaderRef } className="h-4"></div>
        </PageContainer>
    );
};

